import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router';
import { isLoggedIn } from 'common/utils/auth';
import { userStore } from 'store/userStore';
import { getUserInfo } from 'common/utils/secureStorage';
import { useAtomValue } from 'jotai';
import { useNativeScheduleSync } from 'hooks/useNativeScheduleSync';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loggedIn, setLoggedIn] = useState<boolean>(false);
    const location = useLocation();

    const ssoUserState = useAtomValue(userStore.ssoUser.stateAtom);

    useEffect(() => {
        const authenticated = isLoggedIn();
        // 게스트 토큰이 있으면 해당 모임에 한해 인증으로 간주
        const guestToken = sessionStorage.getItem('guestToken');
        const guestMeetingId = sessionStorage.getItem('guestMeetingId');
        const isGuestAccess = !!(guestToken && guestMeetingId &&
            location.pathname.includes(guestMeetingId));

        setLoggedIn(authenticated || isGuestAccess);

        if (authenticated && !getUserInfo()) {
            userStore.onGetSsoUser();
        }

        setIsLoading(false);
    }, [location.pathname]);

    // 네이티브 앱 첫 기동(또는 재설치) 시 활성 모임의 측위 스케줄을 일괄 등록
    // — 딥링크로 홈 안 거치고 바로 상세로 들어와도 스케줄 복구
    useNativeScheduleSync(loggedIn);

    // SSO 회원정보 조회 결과 처리
    useEffect(() => {
        if (ssoUserState.status === 'ready' && ssoUserState.data) {
            userStore.onSaveUserInfo(ssoUserState.data);
            userStore.ssoUser.onDefault();
        } else if (ssoUserState.status === 'error') {
            // 사용자 정보 조회 실패해도 페이지 접근은 허용
            // (토큰이 유효하면 API 호출 시 개별적으로 인증됨)
            console.warn('사용자 정보 조회 실패 (페이지 접근은 허용):', ssoUserState.error);
            userStore.ssoUser.onDefault();
        }
    }, [ssoUserState.status, ssoUserState.data]);

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: 'var(--t-surface-sunken)'
            }}>
                <span style={{ color: 'var(--gray500, #666)' }}>로딩 중...</span>
            </div>
        );
    }

    if (!loggedIn) {
        // 모임 상세 접근 시 게스트 로그인 옵션 제공
        const meetingMatch = location.pathname.match(/\/meeting\/(?:detail|location)\/([a-zA-Z0-9-]+)/);
        if (meetingMatch) {
            const meetingId = meetingMatch[1];
            return <Navigate to={`/guest-login/${meetingId}`} replace />;
        }

        // 기타 페이지는 로그인으로 리다이렉트
        const returnUrl = location.pathname + location.search;
        return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
    }

    return <>{children}</>;
};
